
import React, { useState } from "react";
import { Droppable } from "@hello-pangea/dnd";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { KanbanColumn as KanbanColumnType, KanbanCard } from "@/types/database";
import { TrelloCardItem as TrelloCard } from "./TrelloCardItem";
import { TrelloCardDialog } from "./TrelloCardDialog";
import { ColumnOptionsDropdown } from "./ColumnOptionsDropdown";
import { PlusCircle, Target, Flag, GripVertical } from "lucide-react";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";

interface TrelloColumnProps {
  column: KanbanColumnType;
  cards: KanbanCard[];
  onRefreshCards: () => void;
  onRefreshColumns: () => void;
  boardId: string;
  index: number;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
}

export function TrelloColumn({
  column,
  cards,
  onRefreshCards,
  onRefreshColumns,
  boardId,
  index,
  dragHandleProps
}: TrelloColumnProps) {
  const [showAddCard, setShowAddCard] = useState(false);
  const [goalData, setGoalData] = useState<any>(null);
  
  // Função para buscar dados da meta se esta é uma coluna meta
  const loadGoalData = async () => {
    if (column.is_goal_column && column.goal_id) {
      try {
        const { data, error } = await supabase
          .from('goals')
          .select('*')
          .eq('id', column.goal_id)
          .single();
        
        if (error) {
          console.error("Error loading goal data:", error);
          return;
        }
        
        setGoalData(data);
      } catch (error) {
        console.error("Error loading goal data:", error);
      }
    }
  };

  // Função para criar notificação
  const createGoalCompletedNotification = async (goalTitle: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('notifications')
        .insert({
          user_id: user.id,
          type: 'goal_completed',
          title: 'Meta Concluída!',
          message: `A meta "${goalTitle}" foi concluída com sucesso!`,
          data: {
            goal_id: column.goal_id,
            column_id: column.id,
            goal_title: goalTitle
          }
        });
      
      console.log("Goal completion notification created");
    } catch (error) {
      console.error("Error creating notification:", error);
    }
  };

  // Função para remover vinculação da meta quando concluída
  const removeGoalFromColumn = async () => {
    try {
      const { error } = await supabase
        .from('kanban_columns')
        .update({
          is_goal_column: false,
          goal_id: null
        })
        .eq('id', column.id);

      if (error) {
        console.error("Error removing goal from column:", error);
      } else {
        console.log("Goal removed from column after completion");
        onRefreshColumns();
      }
    } catch (error) {
      console.error("Error removing goal from column:", error);
    }
  };

  // Carregar dados da meta quando o componente monta ou quando a coluna muda
  React.useEffect(() => {
    loadGoalData();
  }, [column.is_goal_column, column.goal_id]);

  // Atualizar contagem da meta em tempo real
  React.useEffect(() => {
    if (column.is_goal_column && column.goal_id && goalData) {
      const updateGoalProgress = async () => {
        const cardsCount = cards.length;
        
        try {
          // Verificar se a meta já foi marcada como concluída
          if (goalData.completed) {
            return; // Se já está concluída, não fazer nada
          }

          const isNowCompleted = cardsCount >= goalData.target_value;
          
          const { error } = await supabase
            .from('goals')
            .update({
              current_value: cardsCount,
              completed: isNowCompleted,
              updated_at: new Date().toISOString()
            })
            .eq('id', column.goal_id);

          if (error) {
            console.error("Error updating goal progress:", error);
          } else {
            // Verificar se a meta foi concluída agora
            if (isNowCompleted && !goalData.completed) {
              // Criar notificação
              await createGoalCompletedNotification(goalData.title);
              
              // Mostrar toast de sucesso
              toast.success(`🎉 Meta "${goalData.title}" concluída!`);
              
              // Remover vinculação da meta da coluna após um delay
              setTimeout(() => {
                removeGoalFromColumn();
              }, 3000); // Aguarda 3 segundos para dar tempo da notificação aparecer
            }
            
            // Recarregar dados da meta
            loadGoalData();
          }
        } catch (error) {
          console.error("Error updating goal progress:", error);
        }
      };

      updateGoalProgress();
    }
  }, [cards.length, column.is_goal_column, column.goal_id, goalData?.target_value, goalData?.completed]);
  
  const handleAddCard = () => {
    setShowAddCard(true);
  };

  const progressPercentage = goalData?.target_value 
    ? Math.min((goalData.current_value / goalData.target_value) * 100, 100)
    : 0;
  
  return (
    <div className="w-[280px] flex-shrink-0">
      <Card className={`border-2 ${
        column.is_goal_column 
          ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 shadow-lg' 
          : 'border-gray-200 bg-card'
      }`}>
        <CardHeader className="px-3 py-2">
          <div className="flex justify-between items-start">
            <div className="flex flex-col gap-2 flex-1">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <div 
                  {...dragHandleProps}
                  className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted/50 transition-opacity"
                >
                  <GripVertical className="h-3 w-3 text-muted-foreground" />
                </div>
                {column.is_goal_column && <Flag className="h-4 w-4 text-blue-600" />}
                {column.name} 
                <span className="text-xs text-muted-foreground ml-1">({cards.length})</span>
              </CardTitle>
              
              {column.is_goal_column && (
                <div className="space-y-2">
                  <Badge variant="secondary" className="text-xs w-fit bg-blue-100 text-blue-700 border-blue-200">
                    <Target className="h-3 w-3 mr-1" />
                    Coluna Meta
                  </Badge>
                  
                  {goalData && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-blue-700 font-medium">{goalData.title}</span>
                        <span className="text-blue-600">
                          {goalData.current_value}/{goalData.target_value}
                        </span>
                      </div>
                      <Progress 
                        value={progressPercentage} 
                        className="h-2 bg-blue-200"
                      />
                      <div className="text-xs text-blue-600">
                        {goalData.completed ? '✅ Meta concluída!' : `${Math.round(progressPercentage)}% concluído`}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <ColumnOptionsDropdown 
              columnId={column.id}
              columnName={column.name}
              boardId={boardId}
              isGoalColumn={column.is_goal_column}
              goalId={column.goal_id}
              onRefresh={() => {
                onRefreshColumns();
                loadGoalData();
              }}
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Droppable droppableId={column.id} type="card">
            {(droppableProvided, snapshot) => (
              <div 
                className={`p-3 ${
                  snapshot.isDraggingOver 
                    ? column.is_goal_column 
                      ? "bg-blue-200/50" 
                      : "bg-secondary/30"
                    : ""
                }`}
              >
                <div
                  {...droppableProvided.droppableProps}
                  ref={droppableProvided.innerRef}
                  className="space-y-3 min-h-[2rem]"
                >
                  {cards.map((card, cardIndex) => (
                    <TrelloCard 
                      key={card.id} 
                      card={card} 
                      index={cardIndex} 
                      onCardUpdated={() => {
                        onRefreshCards();
                        loadGoalData();
                      }} 
                      boardId={boardId}
                      columnId={column.id}
                    />
                  ))}
                  {droppableProvided.placeholder}
                  
                  {!showAddCard && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`w-full justify-start ${
                        column.is_goal_column 
                          ? "text-blue-700 hover:bg-blue-200" 
                          : "text-muted-foreground"
                      }`}
                      onClick={handleAddCard}
                    >
                      <PlusCircle className="h-4 w-4 mr-1" />
                      Adicionar cartão
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Droppable>
          
          {showAddCard && (
            <TrelloCardDialog 
              mode="create"
              boardId={boardId}
              columnId={column.id}
              onCardSaved={() => {
                setShowAddCard(false);
                onRefreshCards();
                loadGoalData();
              }}
              trigger={<></>}
              open={true}
              onOpenChange={(open) => {
                if (!open) setShowAddCard(false);
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
